import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace("https://", "wss://").replace("http://", "ws://");

export const seenSet = (key) => { try { return new Set(JSON.parse(sessionStorage.getItem(key) || "[]")); } catch { return new Set(); } };
export const markSeen = (key, id) => { try { const s = seenSet(key); s.add(id); sessionStorage.setItem(key, JSON.stringify([...s].slice(-50))); } catch { /* private mode */ } };

/**
 * Customer-side live trip events. Listens on the user socket and the app-wide
 * `yorm:booking-updated` re-broadcast (foreground push); when one of `types`
 * lands it fetches the booking and calls `onBooking(booking, { onPage })` if
 * the trip belongs to this customer.
 */
export function useCustomerTripEvents(types, onBooking) {
  const { token, user } = useAuth() || {};
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const cbRef = useRef(onBooking);
  cbRef.current = onBooking;

  useEffect(() => {
    if (!token || user?.role !== "customer") return undefined;
    const mine = new Set([user.id, user.companion_customer_user_id].filter(Boolean));
    const wanted = new Set(types);
    const check = async (bookingId) => {
      if (!bookingId) return;
      try {
        const { data } = await axios.get(`${API}/rideshare/bookings/${bookingId}?token=${token}`);
        if (data?.id && mine.has(data.customer_id)) cbRef.current(data, { onPage: pathRef.current.includes(data.id) });
      } catch { /* not mine / gone */ }
    };
    const onUpdated = (ev) => { const d = ev?.detail || {}; if (wanted.has(String(d.type || ""))) check(d.booking_id); };
    window.addEventListener("yorm:booking-updated", onUpdated);
    let ws = null; let retry = null; let closed = false;
    const connect = () => {
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws/${user.id}?token=${token}`);
        ws.onmessage = (e) => {
          try {
            const d = JSON.parse(e.data);
            if (wanted.has(String(d?.type || ""))) setTimeout(() => check(d.booking_id), 300);
          } catch (_) { /* non-JSON ping */ }
        };
        ws.onclose = () => { if (!closed) retry = setTimeout(connect, 5000); };
      } catch (_) { retry = setTimeout(connect, 5000); }
    };
    connect();
    return () => {
      closed = true; clearTimeout(retry);
      try { ws?.close(); } catch (_) { /* already closed */ }
      window.removeEventListener("yorm:booking-updated", onUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id, user?.role, user?.companion_customer_user_id, types.join("|")]);
}
